import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { CheckBox } from '../Form';
import { getClickAttrs } from '../Analytics/gtag';

import styles from './searchableMultiSelectPopOverMenu.scss';

const SearchableMultiSelectPopOverMenu = (props) => {
    const {
        selectedValues,
        anchorClass,
        popOverMenuClass,
        children,
        alignLeft: forceAlignLeft,
        mobileAlignLeft,
        horizontalEllipsis,
        menuItems,
        onSelected,
        attributeToSave,
        attributeToShow,
        isSearchable,
        'data-testid': dataTestId,
    } = props;
    const [isMenuShowed, setIsMenuShowed] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [menuPositionParams, setMenuPositionParams] = useState({
        displayMenuBelowAnchor: true,
        alignLeft: forceAlignLeft,
    });
    const [selectedMenuItems, setSelectedMenuItems] = useState([]);

    const wrapperRef = useRef(null);

    const handleAnchorClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        let showBelow = true;
        if (ev.nativeEvent.clientY > (2 * window.innerHeight / 3)) {
            showBelow = false;
        }

        setIsMenuShowed(true);
        setMenuPositionParams({
            displayMenuBelowAnchor: showBelow,
            alignLeft: forceAlignLeft,
        });
    };

    const updateMenuItemSelection = (menuItem, ev) => {
        if (menuItem.onlySingleSelect) {
            onSelected([menuItem], ev);
            setIsMenuShowed(false);
            setSelectedMenuItems([]);
            setSearchText('');
            return;
        }

        const nonSingleSelectedItems = selectedMenuItems.filter((item) => !item.onlySingleSelect);
        const isItemSelected = nonSingleSelectedItems.find((sItem) => sItem[attributeToSave] === menuItem[attributeToSave]);
        if (isItemSelected) {
            const updatesSelectedMenuItems = nonSingleSelectedItems.filter((item) => item[attributeToSave] !== menuItem[attributeToSave]);
            setSelectedMenuItems(updatesSelectedMenuItems);
        }

        if (!isItemSelected) {
            setSelectedMenuItems([...nonSingleSelectedItems, menuItem]);
        }
    };

    const onMenuItemClick = (menuItem) => {
        return (ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            updateMenuItemSelection(menuItem, ev);
        };
    };

    const onItemCheckChange = (menuItem) => {
        return (checked, ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            updateMenuItemSelection(menuItem, ev);
        };
    };

    const handleClickOutside = useCallback((event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target) && isMenuShowed) {
            event.stopPropagation();
            event.preventDefault();
            setIsMenuShowed(false);
            setSelectedMenuItems([]);
            setSearchText('');
        }
    }, [wrapperRef, isMenuShowed]);

    const onChangeSearchText = (ev) => {
        setSearchText(ev.target.value);
    };

    const resetSearchText = () => {
        setSearchText('');
    };

    const filteredMenuItems = useMemo(() => {
        return menuItems.filter((menuItem) => {
            if (searchText) {
                return menuItem.label.toLowerCase().includes(searchText.toLowerCase());
            }

            return true;
        });
    }, [menuItems, searchText]);

    const onCancelClick = () => {
        setIsMenuShowed(false);
        setSelectedMenuItems([]);
        setSearchText('');
    };

    const onApplyClick = (ev) => {
        onSelected(selectedMenuItems, ev);
        setIsMenuShowed(false);
        setSelectedMenuItems([]);
        setSearchText('');
    };

    const shouldDisableApplyButton = useMemo(() => {
        const defaultSelectedMenuItems = menuItems?.filter((item) => selectedValues.includes(item[attributeToSave]));
        return selectedMenuItems?.length === defaultSelectedMenuItems.length && selectedMenuItems?.every((item) => defaultSelectedMenuItems.find((defaultItem) => defaultItem[attributeToSave] === item[attributeToSave]));
    }, [selectedValues, menuItems, selectedMenuItems, attributeToSave]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchend', handleClickOutside, false);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchend', handleClickOutside, false);
        };
    }, [handleClickOutside]);

    const scrollOffsetForList = 52; // offset for scrollto top, 52 is height of search input at top
    useEffect(() => {
        setSelectedMenuItems(menuItems.filter((item) => selectedValues.includes(item[attributeToSave])));

        if (selectedValues.length === 1 && isMenuShowed) {
            const selectedMenuItem = menuItems.find(item => item[attributeToSave] === selectedValues[0]);
            const list = document.getElementById('list');
            if (selectedMenuItem) {
                const element = document.getElementById(`item-${selectedMenuItem.value}`);
                if (list.scrollHeight > list.clientHeight && element && list) {
                    list.scrollTop = element.offsetTop - scrollOffsetForList;
                }
            }
        }
    }, [selectedValues, menuItems, isMenuShowed, attributeToSave]);

    return (
        <div
            className={`${styles.popOverMenu} ${popOverMenuClass || ''} ${children ? '' : styles.ellipsis}`}
            data-testid={dataTestId}
        >
            <div
                className={`popovermenu-anchor ${anchorClass || ''}`}
                onClick={handleAnchorClick}
                data-testid={dataTestId && `${dataTestId}.anchor`}
            >
                {children}
                {!children && (
                    <span className={`far ${horizontalEllipsis ? 'fa-ellipsis-h' : 'fa-ellipsis-v'}`} />
                )}
            </div>
            {isMenuShowed && (
                <div className="anchor-click-disabler" />
            )}
            <div
                className={`menu-container ${menuPositionParams.displayMenuBelowAnchor ? 'below-anchor' : 'above-anchor'} ${menuPositionParams.alignLeft ? 'align-left' : 'align-right'} ${mobileAlignLeft} `}
                ref={wrapperRef}
            >
                {isMenuShowed && (
                    <div
                        className={`popovermenu-list ${popOverMenuClass || ''} `}
                    >
                        {isSearchable && (
                            <div className="menu-list-search">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={onChangeSearchText}
                                />
                                <i className={`uts uts-close ${searchText ? 'd-block' : 'd-none'}`} onClick={resetSearchText} data-testid={dataTestId && `${dataTestId}.clear-search-btn`} />
                            </div>
                        )}
                        <div
                            id="list"
                            className="menu-list"
                        >
                            {filteredMenuItems.map((menuItem) => {
                                const isItemSelected = selectedMenuItems.find((selectedMenuItem) => {
                                    return selectedMenuItem[attributeToSave] === menuItem[attributeToSave];
                                });

                                return (
                                    <div
                                        id={`item-${menuItem[attributeToSave]}`}
                                        key={menuItem[attributeToSave]}
                                        className="menu-item"
                                        onClick={onMenuItemClick(menuItem)}
                                        {...getClickAttrs({
                                            category: menuItem.clickCategory,
                                            context: menuItem.clickContext,
                                            location: menuItem.clickLocation,
                                        })}
                                    >
                                        <div className={`d-flex align-items-center`}>
                                            {!menuItem.onlySingleSelect && (
                                                <CheckBox
                                                    id={`select-${menuItem[attributeToSave]}`}
                                                    formGroupCssClass={`mb0`}
                                                    controlled={true}
                                                    checked={isItemSelected}
                                                    onChange={onItemCheckChange(menuItem)}
                                                    disabled={false}
                                                    data-testid="searchablemultiselectpopovermenu.select-item-checkbox"
                                                />
                                            )}
                                            <span className={`flex-1 mw0 ellipsis ${isItemSelected ? 'text-blue' : ''}`}>
                                                {menuItem[attributeToShow]}
                                            </span>
                                            {menuItem.playing && (
                                                <span className={`tag tag-green tag-xs user-status`} data-testid="searchablemultiselectpopovermenu.span-237">Playing</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredMenuItems.length === 0 && (
                                <div className="no-results" data-testid="searchablemultiselectpopovermenu.div-244">
                                    No items to display
                                </div>
                            )}
                        </div>
                        <div className="menu-list-actions">
                            <button
                                className="btn btn-default flex-1"
                                type="button"
                                onClick={onCancelClick}
                                data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary flex-1"
                                type="button"
                                onClick={onApplyClick}
                                disabled={shouldDisableApplyButton}
                                data-testid={dataTestId && `${dataTestId}.apply-btn`}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

SearchableMultiSelectPopOverMenu.propTypes = {
    mobileAlignLeft: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    menuItems: PropTypes.arrayOf(PropTypes.any).isRequired,
    selectedValues: PropTypes.arrayOf(PropTypes.string),
    alignLeft: PropTypes.bool,
    onSelected: PropTypes.func.isRequired,
    popOverMenuClass: PropTypes.string,
    anchorClass: PropTypes.string,
    horizontalEllipsis: PropTypes.bool,
    children: PropTypes.node,
    attributeToSave: PropTypes.string,
    attributeToShow: PropTypes.string,
    isSearchable: PropTypes.bool,
};

SearchableMultiSelectPopOverMenu.defaultProps = {
    mobileAlignLeft: false,
    selectedValues: [],
    alignLeft: false,
    popOverMenuClass: '',
    anchorClass: '',
    horizontalEllipsis: false,
    children: null,
    attributeToSave: '',
    attributeToShow: '',
    isSearchable: true,
};

export default SearchableMultiSelectPopOverMenu;
